<template>
  <DSAssetTree
    v-model:search="search"
    :current-path
    :data="isSkeletonVisible ? undefined : data"
    :loading="isSkeletonVisible"
    :formatter
    :get-connector-info
    :get-description="getDescription"
    :get-linked-account-info
    :repo-name="repoName"
    @click:connector="handleClickConnector"
    @click:name="openEntity"
    @click:path="handleSetPath"
    @collapse="emit('collapse')"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, markRaw, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { get } from 'lodash'

import { LinkedDataConnectorStatus } from '@types'

import { ASSET_FIELD } from '@/entities/assets/utils/const'
import { UNKNOWN_VALUE } from '@/const/common'
import { ROUTE_NAME } from '@/const'

import useAnalyticsStore from '@/store/analytics'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useLinkedDataStore } from '@/store/linkedData'
import { useModalsStore } from '@/store/modals'
import { useRepositoriesStore } from '@/store/repositories/index'
import { useSettingsStore } from '@/store/settings'

import RepositoryPopup from '@/modules/modals/Repositories/Data/RepositoryPopup.vue'
import { DSAssetTree } from '@ds'

defineOptions({ name: 'MainTree' })

const emit = defineEmits<{ collapse: [] }>()

const analyticsStore = useAnalyticsStore()
const assetsBunchStore = useAssetsBunchStore()
const linkedDataStore = useLinkedDataStore()
const modalsStore = useModalsStore()
const repositoriesStore = useRepositoriesStore()
const settingsStore = useSettingsStore()

const route = useRoute()
const router = useRouter()

const assetId = ref<string>()

const assetsBunch = computed(() => assetsBunchStore.getList)
const data = computed(() =>
  settingsStore.getOwnershipAdjustedValues
    ? analyticsStore.module.tree.getLookThroughPreparedData
    : analyticsStore.module.tree.getPreparedData,
)
const isLoading = computed(
  () =>
    !analyticsStore.getError &&
    (!analyticsStore.module.tree.isInit ||
      analyticsStore.module.tree.isLoading),
)
const isAssetsBunchInited = computed(() => assetsBunchStore.initFlag)

const isSkeletonVisible = computed(
  () => isLoading.value || !isAssetsBunchInited.value,
)

const repoName = computed(() => repositoriesStore.getCurrentName)

const currentPath = computed(() => analyticsStore.getPath)

const search = computed({
  get: () => repositoriesStore.getTreeSearch,
  set: value => repositoriesStore.setTreeSearch(value || ''),
})

const formatter = (key: string) => {
  const asset = assetsBunch.value.get(key)
  return asset?.field<string>(ASSET_FIELD.NAME).value || UNKNOWN_VALUE
}

const getDescription = (key: string) => {
  const asset = assetsBunch.value.get(key)
  return asset?.field<string>(ASSET_FIELD.TICKER).value
}

const getConnectorInfo = (key: string) => {
  const asset = assetsBunch.value.get(key)
  if (!asset?.isLinked) return
  const connectorStatus = asset?.field<LinkedDataConnectorStatus>(ASSET_FIELD.CONNECTOR_STATUS).value
  const connectorStatusMessage = asset?.field<string>(ASSET_FIELD.CONNECTOR_STATUS_MESSAGE)
    .value
  switch (connectorStatus) {
    case LinkedDataConnectorStatus.ERROR:
    case LinkedDataConnectorStatus.LOGIN_REQUIRED:
      return {
        status: connectorStatus,
        message: connectorStatusMessage,
      }
    case LinkedDataConnectorStatus.WARNING: {
      const linkedAccountStatus = asset?.field<LinkedDataConnectorStatus>(ASSET_FIELD.LINKED_ACCOUNT_STATUS).value
      if (!linkedAccountStatus) return {}
      const linkedAccountStatusMessage = asset?.field<string>(ASSET_FIELD.LINKED_ACCOUNT_STATUS_MESSAGE).value
      return {
        status: LinkedDataConnectorStatus.ERROR,
        message: linkedAccountStatusMessage,
      }
    }
    default:
      return {}
  }
}

const getLinkedAccountInfo = (key: string) => {
  const asset = assetsBunch.value.get(key)
  if (!asset?.isLinked) return
  return linkedDataStore.getAccount(asset?.data.linked_account_id as string)
}

const handleClickConnector = (path: string[]) => {
  assetId.value = path.at(-1)
  if (!assetId.value) return
  const instance = assetsBunch.value.get(assetId.value)
  instance?.openConnectorInfo(assetId.value, () => {
    assetId.value = undefined
  })
}

const openEntity = (path: string[], isLeaf: boolean) => {
  if (path.length === 1) {
    const repositoryId = repositoriesStore.getCurrentId
    if (!repositoryId) return
    const modalInstance = modalsStore.init(
      `repository-${repositoryId}`,
      markRaw(RepositoryPopup),
    )

    modalInstance?.open(modalsStore.getZIndex(), {
      repository: repositoriesStore.getRepository,
    })
  } else {
    assetId.value = path.at(-1)
    if (!assetId.value) return
    const instance = assetsBunch.value.get(assetId.value)
    const rawAmount = get(analyticsStore.module.tree.getPreparedData, path)?.amount
    instance?.openDrawer(
      path.slice(1),
      rawAmount,
      () => {
        assetId.value = undefined
      },
      isLeaf
    )
  }
}

const handleSetPath = (path: string[]) => {
  if (route.name !== ROUTE_NAME.DASHBOARDS_DASHBOARD) {
    router.push({ name: ROUTE_NAME.DASHBOARDS })
  }
  analyticsStore.setPath(path)
}

onMounted(() => {
  analyticsStore.setActive('Tree', ['tree'])
})

onUnmounted(() => {
  analyticsStore.setInActive('Tree')
})
</script>
